import { useState, useCallback, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Send, RefreshCw, AlertTriangle, CheckCircle2, MessageCircleMore, CheckCheck, PartyPopper, RotateCcw, Info, Copy, Check, Mail, Clock } from "lucide-react";
import { UploadZone } from "../components/UploadZone";
import { ImportSource } from "../components/ImportSource";
import { CustomerTable } from "../components/CustomerTable";
import { Stepper } from "../components/Stepper";
import { ScheduleStep } from "../components/ScheduleStep";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { uploadBatch, sendBatch, getHealth, importFromGSheet, updateCustomer, switchTwilioMode, api } from "../lib/api";

const DEFAULT_MSG =
  "שלום {name},\nמצורף חשבון עסקה על סך {amount}₪ עבור: {description}\nתודה רבה!";

const DOC_TYPE_LABELS = {
  tax_invoice: "שליחת חשבונית מס",
  tax_invoice_receipt: "שליחת חשבונית מס / קבלה",
  receipt: "שליחת קבלה",
  credit_invoice: "שליחת חשבונית זיכוי",
  price_quote: "שליחת הצעת מחיר",
  transaction_account: "שליחת חשבון עסקה",
  work_order: "שליחת הזמנת עבודה",
  delivery_note: "שליחת תעודת משלוח",
  return_note: "שליחת תעודת החזרה",
  purchase_order: "שליחת הזמנת רכש",
};

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const docType = searchParams.get("doc") || "transaction_account";
  const companyId = searchParams.get("company");
  // Channel persistence: URL param wins, then last user choice from localStorage,
  // then default to whatsapp. This keeps the email channel sticky when the user
  // navigates between document types via the sidebar.
  const channel = searchParams.get("channel")
    || (typeof window !== "undefined" ? localStorage.getItem("flowup_channel") : null)
    || "whatsapp";
  const docTypeLabel = DOC_TYPE_LABELS[docType] || "שליחת מסמך";
  const [batch, setBatch] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Confirmation popup that appears AFTER the user clicks "תזמן משלוח" — it
  // summarises the scheduled details and the user clicks "שלח" to commit.
  const [scheduleConfirmOpen, setScheduleConfirmOpen] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MSG);
  const [sendEmailToo, setSendEmailToo] = useState(false);
  const [addEmailOpen, setAddEmailOpen] = useState(false);
  const [brevoApiKey, setBrevoApiKey] = useState("");
  const [brevoFromEmail, setBrevoFromEmail] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);
  const [company, setCompany] = useState(null);
  const [health, setHealth] = useState(null);
  const [schedule, setSchedule] = useState({ mode: "now", scheduledAt: "", intervalSeconds: 5 });
  const [rocketLaunched, setRocketLaunched] = useState(false); // 🚀 send animation

  // 5-stage verification stepper state — drives the new vertical Stepper.
  // Indices: 0=Sheet, 1=WhatsApp, 2=Meta, 3=GreenInvoice, 4=Schedule
  const [stages, setStages] = useState([
    { status: "idle" },
    { status: "idle" },
    { status: "idle" },
    { status: "idle" },
    { status: "idle" },
  ]);
  const updateStage = (idx, patch) =>
    setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [savedSheets, setSavedSheets] = useState([]);

  const handleChannelChange = (next) => {
    if (next === "sms") {
      toast.info("ערוץ SMS אינו כלול במנוי הנוכחי. צרי קשר לשדרוג.");
      return;
    }
    // Persist user's last channel choice so it sticks across navigations.
    try { localStorage.setItem("flowup_channel", next); } catch { /* no-op */ }
    const params = new URLSearchParams(searchParams);
    params.set("channel", next);
    setSearchParams(params, { replace: true });
    setResults({});
  };

  // Load the company (to know if it has an email provider configured)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const c = (data.companies || []).find((x) => x.id === companyId);
        setCompany(c || null);
      } catch { setCompany(null); }
    })();
  }, [companyId]);

  // Persist channel from URL → localStorage so it survives page reloads.
  useEffect(() => {
    const fromUrl = searchParams.get("channel");
    if (fromUrl) {
      try { localStorage.setItem("flowup_channel", fromUrl); } catch { /* no-op */ }
    }
  }, [searchParams]);

  // Fetch the saved sheets so step 1 can auto-load the first one.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sheets");
        setSavedSheets(data.sheets || []);
      } catch { setSavedSheets([]); }
    })();
  }, []);

  const hasEmailProvider = !!(company && (company.brevo_api_key || company.resend_api_key));
  const customersWithEmail = customers.filter((c) => c.email && String(c.email).includes("@"));

  // Derive current step for stepper
  const currentStep = (() => {
    if (Object.keys(results).length > 0) return 3; // sent
    if (confirmOpen) return 2; // confirming
    if (customers.length > 0) return 1; // list loaded
    return 0; // upload
  })();

  // Did the most recent send succeed fully, partially, or fully fail?
  const sendStatus = (() => {
    const total = Object.keys(results).length;
    if (total === 0) return "all_ok";
    const success = Object.values(results).filter((r) => r.whatsapp_ok || r.email_ok).length;
    if (success === 0) return "all_fail";
    if (success < total) return "partial";
    return "all_ok";
  })();

  useEffect(() => {
    loadHealth();
  }, [companyId]);

  function loadHealth() {
    getHealth(companyId)
      .then(setHealth)
      .catch(() => setHealth({ ok: false }));
  }

  const handleUpload = useCallback(async (file) => {
    setUploading(true);
    updateStage(0, { status: "loading" });
    try {
      const data = await uploadBatch(file);
      setBatch(data);
      setCustomers(data.customers);
      const valid = data.customers.filter((c) => c.phone_valid).map((c) => c.id);
      setSelectedIds(new Set(valid));
      setResults({});
      updateStage(0, { status: "verified", info: `${data.customer_count} לקוחות נטענו` });
      toast.success(`נטענו ${data.customer_count} לקוחות`);
    } catch (e) {
      updateStage(0, { status: "error", error: e.response?.data?.detail || "שגיאה בטעינה" });
      toast.error(e.response?.data?.detail || "שגיאה בטעינת הקובץ");
    } finally {
      setUploading(false);
    }
  }, []);

  const handleGSheet = useCallback(async (url) => {
    setUploading(true);
    updateStage(0, { status: "loading" });
    try {
      const data = await importFromGSheet(url, { company_id: companyId, doc_type: docType });
      setBatch(data);
      setCustomers(data.customers);
      const valid = data.customers.filter((c) => c.phone_valid).map((c) => c.id);
      setSelectedIds(new Set(valid));
      setResults({});
      updateStage(0, { status: "verified", info: `${data.customer_count} לקוחות נטענו` });
      toast.success(`נטענו ${data.customer_count} לקוחות מ-Google Sheets`);
    } catch (e) {
      updateStage(0, { status: "error", error: e.response?.data?.detail || "שגיאה בטעינה" });
      toast.error(e.response?.data?.detail || "שגיאה בטעינת הגיליון");
    } finally {
      setUploading(false);
    }
  }, [companyId, docType]);

  // Run /api/brevo/verify-all once and use the response to mark BOTH the
  // WhatsApp stage (step 1) and the Meta-business stage (step 2) at once.
  const verifyBrevoStages = useCallback(async () => {
    if (!company?.brevo_api_key) {
      updateStage(1, { status: "error", error: "אין מפתח Brevo בחברה" });
      updateStage(2, { status: "error", error: "אין מפתח Brevo בחברה" });
      return;
    }
    updateStage(1, { status: "loading" });
    updateStage(2, { status: "loading" });
    try {
      const { data } = await api.post("/brevo/verify-all", {
        brevo_api_key: company.brevo_api_key,
        brevo_whatsapp_from: company.brevo_whatsapp_from || "",
        expected_email: company.email || company.owner_email || "",
      });
      const wa = data?.integrations?.whatsapp;
      const waba = wa?.waba || {};
      const phone = waba.phoneNumber || waba.phone || company.brevo_whatsapp_from || "";
      const businessName = waba.verifiedName || waba.businessName || waba.displayName
        || data?.integrations?.account?.company || company.name;
      const wabaId = waba.businessAccountId || waba.wabaId || "";

      if (wa?.ok) {
        updateStage(1, {
          status: "verified",
          info: phone ? `${phone}` : "מאומת ב-Brevo",
        });
        updateStage(2, {
          status: "verified",
          info: businessName + (wabaId ? ` • ${wabaId}` : ""),
        });
      } else {
        updateStage(1, { status: "error", error: wa?.detail || "WhatsApp לא מאומת" });
        updateStage(2, { status: "error", error: wa?.detail || "Meta לא מאומת" });
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "שגיאה באימות Brevo";
      updateStage(1, { status: "error", error: msg });
      updateStage(2, { status: "error", error: msg });
    }
  }, [company]);

  const verifyGreenInvoice = useCallback(async () => {
    updateStage(3, { status: "loading" });
    try {
      const data = await getHealth(companyId);
      setHealth(data);
      if (data?.green_invoice?.ok) {
        updateStage(3, { status: "verified", info: "Green Invoice מחובר" });
      } else {
        updateStage(3, {
          status: "error",
          error: data?.green_invoice?.error || "Green Invoice לא מחובר",
        });
      }
    } catch (e) {
      updateStage(3, { status: "error", error: e.response?.data?.detail || "שגיאה" });
    }
  }, [companyId]);

  const handleStepClick = (idx) => {
    // Block further progress if any earlier stage is in error state — the
    // user must contact support, fix the issue, and refresh to retry.
    const blockingErrorIdx = stages.findIndex((s, i) => i < idx && s?.status === "error");
    if (blockingErrorIdx !== -1) {
      toast.error("יש לפתור את התקלה בשלב הקודם לפני שתוכלי להמשיך");
      return;
    }
    if (idx === 0) {
      // Either re-load the sheet or scroll to the table if already loaded.
      if (batch) {
        const el = document.querySelector('[data-testid="customers-table"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      // No batch yet — auto-load the first saved sheet if one exists.
      if (savedSheets.length > 0 && savedSheets[0].url) {
        handleGSheet(savedSheets[0].url);
      } else {
        toast.info("אין גיליון שמור — הוסיפי גיליון בהגדרות");
      }
      return;
    }
    if (idx === 1 || idx === 2) { verifyBrevoStages(); return; }
    if (idx === 3) { verifyGreenInvoice(); return; }
    if (idx === 4) {
      setScheduleDialogOpen(true);
      return;
    }
  };

  // Remove duplicate rows from the loaded batch.  A row is considered
  // a duplicate when its phone (whatsapp/sms) or email (email channel) is
  // already present.  Falls back to a name+description+amount fingerprint
  // when neither contact field is filled.
  const handleDeduplicate = useCallback(() => {
    const seen = new Set();
    const isEmailCh = channel === "email";
    const kept = [];
    let removed = 0;
    for (const c of customers) {
      const key = (isEmailCh
        ? (c.email || "").trim().toLowerCase()
        : (c.phone_e164 || c.phone_raw || "").trim()
      ) || `${c.name}|${c.description}|${c.amount}`;
      if (seen.has(key)) { removed += 1; continue; }
      seen.add(key);
      kept.push(c);
    }
    if (removed === 0) {
      toast.info("לא נמצאו כפילויות");
      return;
    }
    setCustomers(kept);
    const validIds = kept
      .filter((c) => isEmailCh ? (c.email && String(c.email).includes("@")) : c.phone_valid)
      .map((c) => c.id);
    setSelectedIds(new Set(validIds));
    toast.success(`הוסרו ${removed} כפילויות • נשארו ${kept.length} לקוחות`);
  }, [customers, channel]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (checked) => {
      if (checked) {
        const validIds = customers.filter((c) => c.phone_valid).map((c) => c.id);
        setSelectedIds(new Set(validIds));
      } else {
        setSelectedIds(new Set());
      }
    },
    [customers]
  );

  const handleSend = async () => {
    if (!batch || selectedIds.size === 0) return;
    // If the user picked "תזמון משלוח" mode, don't send straight away — close
    // the main confirm dialog and open a small summary popup that gives them
    // one last chance to review before actually scheduling.
    if (schedule.mode === "scheduled" && schedule.scheduledAt) {
      setConfirmOpen(false);
      setScheduleConfirmOpen(true);
      return;
    }
    await commitSend();
  };

  // The actual API call. Called either directly (immediate send) or after the
  // user confirms in the schedule-summary popup.
  const commitSend = async () => {
    setConfirmOpen(false);
    setScheduleConfirmOpen(false);
    if (!batch || selectedIds.size === 0) return;
    // 🚀 Launch the rocket animation right when the user confirms.
    setRocketLaunched(true);
    window.setTimeout(() => setRocketLaunched(false), 2200);
    setSending(true);
    try {
      // ============ EMAIL CHANNEL ============
      if (channel === "email") {
        const subject_template = `${docTypeLabel} - {name}`;
        // Build the per-customer email payload, including scheduling and
        // throttle settings (matches the WhatsApp branch behavior).
        const payload = {
          company_id: companyId,
          batch_id: batch.batch_id,
          subject_template,
          body_template: message,
          doc_type: docType,
          customer_ids: Array.from(selectedIds),
          interval_seconds: schedule.intervalSeconds,
        };
        if (schedule.mode === "scheduled" && schedule.scheduledAt) {
          payload.scheduled_at = new Date(schedule.scheduledAt).toISOString();
        }
        const { data } = await api.post(`/companies/${companyId}/send-email-batch`, payload);

        // Scheduled response → mark all selected customers as "תוזמן ל-…"
        if (data.scheduled) {
          const dt = new Date(data.scheduled_at);
          const label = `${dt.toLocaleDateString("he-IL")} ${dt.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}`;
          const scheduledMap = {};
          selectedIds.forEach((id) => {
            const c = customers.find((x) => x.id === id);
            scheduledMap[id] = {
              customer_id: id,
              name: c?.name,
              scheduled: true,
              scheduled_at: data.scheduled_at,
              scheduled_label: label,
            };
          });
          setResults(scheduledMap);
          toast.success(`✅ בוצע — ${data.count} הודעות יישלחו ב-${label}`);
          setSending(false);
          return;
        }

        // Immediate response: map details by name+email so the table updates
        const resultMap = {};
        const details = data.details || [];
        const detailByKey = new Map();
        details.forEach((d) => {
          detailByKey.set(`${d.name || ""}|${(d.email || "").toLowerCase()}`, d);
        });
        customers.forEach((c) => {
          const key = `${c.name || ""}|${(c.email || "").toLowerCase()}`;
          const d = detailByKey.get(key);
          if (!d) return;
          resultMap[c.id] = {
            customer_id: c.id,
            name: c.name,
            invoice_ok: !!d.invoice_url,
            email_ok: !!d.ok,
            whatsapp_ok: !!d.ok,
            error: d.error || d.invoice_error || null,
          };
        });
        setResults(resultMap);
        toast.success(`✅ בוצע — נשלחו ${data.sent || 0}/${data.total || customers.length} אימיילים`);
        setSending(false);
        return;
      }

      // ============ WHATSAPP CHANNEL ============
      const opts = {
        send_email_too: sendEmailToo,
        interval_seconds: schedule.intervalSeconds,
      };
      if (schedule.mode === "scheduled" && schedule.scheduledAt) {
        opts.scheduled_at = new Date(schedule.scheduledAt).toISOString();
      }
      const data = await sendBatch(batch.batch_id, Array.from(selectedIds), message, opts);
      if (data.scheduled) {
        const dt = new Date(data.scheduled_at);
        const label = `${dt.toLocaleDateString("he-IL")} ${dt.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}`;
        const scheduledMap = {};
        selectedIds.forEach((id) => {
          const c = customers.find((x) => x.id === id);
          scheduledMap[id] = {
            customer_id: id,
            name: c?.name,
            scheduled: true,
            scheduled_at: data.scheduled_at,
            scheduled_label: label,
          };
        });
        setResults(scheduledMap);
        toast.success(`✅ בוצע — ${data.count} הודעות יישלחו ב-${label}`);
        setSending(false);
        return;
      }
      const resultMap = {};
      data.results.forEach((r) => {
        resultMap[r.customer_id] = r;
      });
      setResults(resultMap);
      toast.success(
        `✅ בוצע — נשלחו ${data.success}/${data.total} הודעות`,
        { description: data.failed > 0 ? `${data.failed} נכשלו - בדוק את הפרטים בטבלה` : "הכל הצליח!" }
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשליחה");
    } finally {
      setSending(false);
    }
  };

  const totalAmount = customers
    .filter((c) => selectedIds.has(c.id))
    .reduce((sum, c) => sum + Number(c.amount || 0), 0);

  const giOk = health?.green_invoice?.ok;
  const twOk = health?.twilio?.ok;

  // Channel-aware page header. The "doc type" label gets a channel suffix so
  // the user always sees which channel they're sending through.
  const channelLabel = channel === "email" ? "באימייל" : "בוואטסאפ";
  const dynamicTitle = (() => {
    const baseLabel = (DOC_TYPE_LABELS[docType] || "שליחת מסמך").replace(/^שליחת\s+/, "");
    return `שלח ${baseLabel}`;
  })();

  // When the user changes doc type from the dropdown
  const handleDocTypeChange = (newDoc) => {
    const params = new URLSearchParams(searchParams);
    params.set("doc", newDoc);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in">
      <Toaster position="top-center" richColors dir="rtl" />

      {/* 🚀 Rocket launch animation — fires when sending starts */}
      {rocketLaunched && (
        <div
          className="fixed inset-0 z-[1000] pointer-events-none flex items-center justify-center overflow-hidden"
          data-testid="rocket-launch-overlay"
        >
          <div className="relative">
            <div className="text-[120px] animate-rocket-launch select-none" style={{ filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.25))" }}>
              🚀
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[60px] animate-rocket-trail">
              💨
            </div>
          </div>
          <p className="absolute bottom-32 left-1/2 -translate-x-1/2 font-heading text-2xl font-bold text-emerald-600 animate-fade-in bg-white/90 px-6 py-3 rounded-full shadow-lg">
            יוצא לדרך! ✨
          </p>
        </div>
      )}

      {/* Channel selector removed — channel is now picked on the Client Dashboard
          (subscription card) before entering this page.  The chosen channel
          arrives via ?channel=... and is persisted in localStorage. */}

      {/* Page header — title with doc-type dropdown selector */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="space-y-2 w-full">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">שלח</h1>
            <select
              value={docType}
              onChange={(e) => handleDocTypeChange(e.target.value)}
              className="font-heading text-sm font-bold tracking-tight bg-emerald-50 border-2 border-emerald-300 rounded-lg px-3 py-1.5 text-emerald-700 hover:bg-emerald-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid="doc-type-dropdown"
            >
              {Object.entries(DOC_TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label.replace(/^שליחת\s+/, "")}</option>
              ))}
            </select>
          </div>
          <span className="text-xs text-stone-500 block">
            טעני גיליון ושלחי לכל הלקוחות בלחיצה אחת
          </span>
        </div>
      </div>

      {/* Two-column layout on lg+: vertical stepper on the side, content on the other */}
      <div className="grid lg:grid-cols-[300px_1fr] gap-6 items-start">
        {/* Workflow stepper — vertical, sticky on lg+ */}
        <div className="lg:sticky lg:top-20 order-1">
          <Stepper stages={stages} onStepClick={handleStepClick} />
        </div>

        {/* Main content column */}
        <div className="space-y-6 order-2 min-w-0">

      {/* Customer Table */}
      {customers.length > 0 && (
        <div data-testid="customers-table">
          <CustomerTable
            customers={customers}
            selectedIds={selectedIds}
            onToggle={toggleSelect}
            onToggleAll={toggleAll}
            results={results}
            channel={channel}
            rawHeaders={batch?.raw_headers || null}
            onDeduplicate={handleDeduplicate}
            onUpdateCustomer={async (customerId, updates) => {
              try {
                const res = await updateCustomer(batch.batch_id, customerId, updates);
                setCustomers((prev) => prev.map((c) => (c.id === customerId ? res.customer : c)));
                toast.success("השינוי נשמר");
              } catch (e) {
                toast.error(e.response?.data?.detail || "שגיאה בעדכון");
              }
            }}
          />
        </div>
      )}

      {/* Empty-state hint when no batch yet — keeps the page tidy.  The
          import wizard is hidden by default and only shown if there's no
          saved sheet at all (full ImportSource fallback). */}
      {customers.length === 0 && savedSheets.length === 0 && (
        <div data-testid="import-source">
          <ImportSource
            onFileUpload={handleUpload}
            onGSheetImport={handleGSheet}
            loading={uploading}
          />
        </div>
      )}
      {customers.length === 0 && savedSheets.length > 0 && (
        <div className="bg-stone-50 border-2 border-dashed border-stone-300 rounded-2xl p-8 text-center" data-testid="empty-hint">
          <p className="text-stone-600">לחצי על הקוביה <strong className="text-orange-600">"טעינת גיליון"</strong> מימין כדי לטעון את הנתונים</p>
        </div>
      )}

      {/* Blocking error banner — any stage in error state freezes the rest of
          the flow.  User must use the support button on the failing stage,
          then refresh the page to start over. */}
      {customers.length > 0 && stages.some((s) => s.status === "error") && (
        <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-4 flex items-start gap-3" data-testid="blocking-error-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-bold text-red-900">לא ניתן להמשיך עד לתיקון התקלה</p>
            <p className="text-red-800 mt-1">
              לחצי על הכפתור הירוק "פנה לתמיכה בוואטסאפ" שמופיע ליד הקוביה האדומה.
              לאחר שהנציג יסדיר את התקלה — רענני את העמוד כדי להתחיל מחדש.
            </p>
          </div>
        </div>
      )}

      {/* All-connected celebratory banner — appears when all 5 stages are verified */}
      {customers.length > 0 && stages.every((s) => s.status === "verified") && Object.keys(results).length === 0 && (
        <div className="bg-gradient-to-l from-emerald-500 to-green-500 rounded-2xl p-5 text-white shadow-xl flex items-center justify-between flex-wrap gap-3 animate-fade-in" data-testid="all-connected-banner">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <div>
              <p className="font-heading text-lg font-bold">הכל מחובר ומוכן לשליחה ✨</p>
              <p className="text-sm text-emerald-50">5/5 חיבורים אומתו בהצלחה</p>
            </div>
          </div>
        </div>
      )}

      {/* Send CTA appears ONLY after schedule (idx 4) is verified AND no stage
          is in error state.  Once anything errors out, the user must contact
          support and refresh — no skipping ahead. */}
      {customers.length > 0
        && stages[4]?.status === "verified"
        && !stages.some((s) => s.status === "error") && (
        <div className="sticky bottom-4 z-30">
          {Object.keys(results).length === 0 ? (
            <div className="bg-white border-2 border-brand rounded-2xl p-4 shadow-lg flex items-center justify-between flex-wrap gap-3" data-testid="send-cta-box">
              <div>
                <p className="font-semibold">
                  {selectedIds.size > 0
                    ? `מוכן לשלוח ל-${selectedIds.size} לקוחות`
                    : "בחר לקוחות לשליחה"}
                </p>
                {selectedIds.size > 0 && (
                  <p className="text-xs text-muted-foreground">
                    סך הסכומים: {totalAmount.toLocaleString("he-IL")} ₪
                  </p>
                )}
              </div>
              <Button
                size="lg"
                disabled={selectedIds.size === 0 || sending}
                onClick={() => setConfirmOpen(true)}
                className={`text-white font-semibold ${channel === "email" ? "bg-blue-600 hover:bg-blue-700" : "bg-whatsapp hover:bg-whatsapp-hover"}`}
                data-testid="send-whatsapp-button"
              >
                {channel === "email" ? <Mail className="h-5 w-5 ml-2" /> : <MessageCircleMore className="h-5 w-5 ml-2" />}
                {sending ? "שולח..." : "שלח"}
              </Button>
            </div>
          ) : (
            <SentSummary results={results} onNewBatch={() => {
              setBatch(null);
              setCustomers([]);
              setSelectedIds(new Set());
              setResults({});
            }} />
          )}
        </div>
      )}

      {/* After-send summary stays visible even before schedule is set, so users
          who already sent something can see results. */}
      {customers.length > 0 && Object.keys(results).length > 0 && stages[4]?.status !== "verified" && (
        <div className="sticky bottom-4 z-30">
          <SentSummary results={results} onNewBatch={() => {
            setBatch(null);
            setCustomers([]);
            setSelectedIds(new Set());
            setResults({});
          }} />
        </div>
      )}

      {/* Schedule dialog — fired by clicking the purple "תזמון" stepper block */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-right text-purple-700">תזמון משלוח</DialogTitle>
            <DialogDescription className="text-right">
              בחרי מתי לשלוח ובאיזה קצב.  ניתן לעדכן גם בזמן אישור השליחה.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <ScheduleStep value={schedule} onChange={setSchedule} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)} data-testid="schedule-cancel">
              ביטול
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => {
                const label = schedule.mode === "scheduled" && schedule.scheduledAt
                  ? new Date(schedule.scheduledAt).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
                  : "מיידית";
                updateStage(4, { status: "verified", info: `${label} • ${schedule.intervalSeconds}ש' בין הודעות` });
                setScheduleDialogOpen(false);
                toast.success("התזמון נשמר");
              }}
              data-testid="schedule-save"
            >
              שמור תזמון
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent dir="rtl" className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-right">ברצונך לשלוח?</DialogTitle>
            <DialogDescription className="text-right">
              סקור את הסיכום הבא לפני אישור סופי
            </DialogDescription>
          </DialogHeader>

          {/* Send summary card */}
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3 space-y-1.5 text-sm" data-testid="send-summary">
            <div className="flex justify-between"><span className="text-stone-600">ערוץ:</span>
              <strong className="text-emerald-700">{channel === "email" ? "אימייל" : channel === "sms" ? "SMS" : "וואטסאפ"}</strong></div>
            <div className="flex justify-between"><span className="text-stone-600">סוג מסמך:</span>
              <strong className="text-emerald-700">{(DOC_TYPE_LABELS[docType] || "").replace(/^שליחת\s+/, "")}</strong></div>
            <div className="flex justify-between"><span className="text-stone-600">תזמון:</span>
              <strong className="text-emerald-700">
                {schedule.mode === "scheduled" && schedule.scheduledAt
                  ? new Date(schedule.scheduledAt).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
                  : "שליחה מיידית"}
              </strong></div>
            <div className="flex justify-between"><span className="text-stone-600">מספר לקוחות:</span>
              <strong className="text-emerald-700">{selectedIds.size}</strong></div>
            <div className="flex justify-between"><span className="text-stone-600">סך הסכומים:</span>
              <strong className="text-emerald-700">{totalAmount.toLocaleString("he-IL")} ₪</strong></div>
          </div>

          <div className="space-y-2 py-2">
            <Label>תוכן ההודעה</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="font-mono text-sm"
              data-testid="message-template-textarea"
            />
            {/* Variable cheat sheet — labels each placeholder with its
                Hebrew meaning + the column letter from the loaded sheet so
                the user always knows what each {placeholder} refers to. */}
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-2.5 space-y-1.5">
              <p className="text-xs font-semibold text-stone-700">משתנים זמינים בתבנית:</p>
              <div className="flex flex-wrap gap-1.5 text-[11px]">
                <span className="inline-flex items-center gap-1 bg-white border border-stone-200 px-2 py-0.5 rounded">
                  <code className="font-mono text-brand">{"{name}"}</code>
                  <span className="text-stone-500">→</span>
                  <span>שם הלקוח</span>
                  <span className="text-[10px] font-bold text-stone-400 bg-stone-100 px-1 rounded ml-0.5">A</span>
                </span>
                <span className="inline-flex items-center gap-1 bg-white border border-stone-200 px-2 py-0.5 rounded">
                  <code className="font-mono text-brand">{"{amount}"}</code>
                  <span className="text-stone-500">→</span>
                  <span>סכום לתשלום</span>
                  <span className="text-[10px] font-bold text-stone-400 bg-stone-100 px-1 rounded ml-0.5">D</span>
                </span>
                <span className="inline-flex items-center gap-1 bg-white border border-stone-200 px-2 py-0.5 rounded">
                  <code className="font-mono text-brand">{"{description}"}</code>
                  <span className="text-stone-500">→</span>
                  <span>תיאור השירות</span>
                  <span className="text-[10px] font-bold text-stone-400 bg-stone-100 px-1 rounded ml-0.5">F</span>
                </span>
                <span className="inline-flex items-center gap-1 bg-white border border-stone-200 px-2 py-0.5 rounded">
                  <code className="font-mono text-brand">{"{number}"}</code>
                  <span className="text-stone-500">→</span>
                  <span>מספר חשבונית</span>
                </span>
              </div>
            </div>

            {/* Schedule already chosen via Stepper step 5 — show a read-only
                summary line here instead of duplicating the picker. */}
            <div className="rounded-lg border border-purple-200 bg-purple-50/40 px-3 py-2 text-xs text-purple-800 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              <span>תזמון:&nbsp;
                <strong>
                  {schedule.mode === "scheduled" && schedule.scheduledAt
                    ? new Date(schedule.scheduledAt).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
                    : "שליחה מיידית"}
                </strong>
                {" • "}
                <span>{schedule.intervalSeconds} ש' בין הודעות</span>
              </span>
            </div>

            {/* Email toggle - only shown when at least 1 customer has email */}
            {customersWithEmail.length > 0 && (
              <div
                className={`rounded-xl border-2 p-3 mt-3 transition-colors ${
                  sendEmailToo ? "bg-blue-50 border-blue-300" : "bg-stone-50 border-stone-200"
                }`}
                data-testid="email-toggle-box"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-2 flex-1 min-w-[200px]">
                    <span className="text-xl">📧</span>
                    <div>
                      <p className="font-bold text-sm text-stone-900">
                        זיהינו {customersWithEmail.length} לקוחות עם אימייל
                      </p>
                      <p className="text-[11px] text-stone-600 leading-snug mt-0.5">
                        {hasEmailProvider
                          ? "הפעילי כדי לשלוח את החשבונית גם במייל (בנוסף לוואטסאפ)"
                          : "כדי להפעיל — צריך לחבר ספק מיילים (Brevo/Resend) בהגדרות החברה"}
                      </p>
                    </div>
                  </div>

                  {hasEmailProvider ? (
                    // Toggle switch
                    <button
                      type="button"
                      role="switch"
                      aria-checked={sendEmailToo}
                      onClick={() => setSendEmailToo((v) => !v)}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors flex-shrink-0 ${
                        sendEmailToo ? "bg-blue-600" : "bg-stone-300"
                      }`}
                      data-testid="email-toggle-switch"
                    >
                      <span
                        className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform ${
                          sendEmailToo ? "-translate-x-1" : "-translate-x-8"
                        }`}
                      />
                      <span className={`absolute text-[9px] font-bold ${sendEmailToo ? "right-1.5 text-white" : "left-1.5 text-stone-600"}`}>
                        {sendEmailToo ? "פעיל" : "כבוי"}
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAddEmailOpen(true)}
                      className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold px-3 py-1.5 rounded-md transition flex-shrink-0"
                      data-testid="add-email-provider-btn"
                    >
                      ⚡ חבר מסלול מיילים עכשיו
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-row-reverse gap-2 sm:gap-2">
            <Button
              onClick={handleSend}
              disabled={schedule.mode === "scheduled" && !schedule.scheduledAt}
              className={`text-white disabled:opacity-50 ${
                schedule.mode === "scheduled"
                  ? "bg-purple-600 hover:bg-purple-700"
                  : channel === "email"
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-whatsapp hover:bg-whatsapp-hover"
              }`}
              data-testid="confirm-send-button"
            >
              {schedule.mode === "scheduled" ? (
                <>
                  <Clock className="h-4 w-4 ml-2" />
                  כן, תזמן משלוח
                </>
              ) : (
                <>
                  <CheckCheck className="h-4 w-4 ml-2" />
                  כן, שלח עכשיו
                </>
              )}
            </Button>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="cancel-send-button">
              לא, ביטול
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule confirmation popup — appears AFTER the user picks date/time
          and clicks "תזמן משלוח" in the main dialog. Shows a final summary
          and the bottom button changes to "שלח!" — clicking it commits.  */}
      <Dialog open={scheduleConfirmOpen} onOpenChange={setScheduleConfirmOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md" data-testid="schedule-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-right flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-600" />
              אישור תזמון משלוח
            </DialogTitle>
            <DialogDescription className="text-right">
              בדקי את פרטי התזמון לפני האישור
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-xl bg-purple-50 border-2 border-purple-200 p-4 space-y-2.5" data-testid="schedule-summary">
              <div className="flex items-center justify-between">
                <span className="text-sm text-purple-900 font-semibold">📅 ייצא ב-</span>
                <span className="text-sm font-bold text-purple-900 font-mono">
                  {schedule.scheduledAt
                    ? new Date(schedule.scheduledAt).toLocaleString("he-IL", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })
                    : ""}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-purple-900 font-semibold">👥 מספר נמענים</span>
                <span className="text-sm font-bold text-purple-900">{selectedIds.size}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-purple-900 font-semibold">⏱ הפסקה בין הודעות</span>
                <span className="text-sm font-bold text-purple-900">
                  {schedule.intervalSeconds === 0 ? "ללא הפסקה" : `${schedule.intervalSeconds} שניות`}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-purple-900 font-semibold">📡 ערוץ</span>
                <span className="text-sm font-bold text-purple-900">
                  {channel === "email" ? "אימייל (Brevo)" : "וואטסאפ (Brevo)"}
                </span>
              </div>
            </div>
            <p className="text-xs text-stone-500 leading-relaxed">
              💡 ניתן לבטל את התזמון בכל שלב לפני זמן המשלוח. ההודעות יישלחו אוטומטית בזמן שהוגדר.
            </p>
          </div>

          <DialogFooter className="flex-row-reverse gap-2 sm:gap-2">
            <Button
              onClick={commitSend}
              disabled={sending}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="schedule-commit-button"
            >
              <CheckCheck className="h-4 w-4 ml-2" />
              {sending ? "שולח..." : "שלח!"}
            </Button>
            <Button variant="outline" onClick={() => setScheduleConfirmOpen(false)} data-testid="schedule-cancel-button">
              חזרה
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Email Provider (Brevo) popup */}
      <Dialog open={addEmailOpen} onOpenChange={setAddEmailOpen}>
        <DialogContent dir="rtl" className="max-w-md" data-testid="add-email-dialog">
          <DialogHeader>
            <DialogTitle>חיבור מסלול מיילים (Brevo)</DialogTitle>
            <DialogDescription>
              הרשמי ב-Brevo (חינם, 9,000 מיילים/חודש) וקבלי מיד API Key שיוצאו המיילים דרכו.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <a
              href="https://app.brevo.com/settings/keys/api"
              target="_blank"
              rel="noreferrer"
              className="block text-center bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 rounded-md text-sm transition"
              data-testid="open-brevo-link"
            >
              פתחי דף ה-API Keys של Brevo
            </a>
            <div>
              <Label>Brevo API Key</Label>
              <Input
                dir="ltr"
                value={brevoApiKey}
                onChange={(e) => setBrevoApiKey(e.target.value)}
                placeholder="xkeysib-xxxxxxxxxxxxxxxxxxxxx"
                className="mt-1 text-right font-mono text-xs"
                data-testid="popup-brevo-api-key"
              />
            </div>
            <div>
              <Label>כתובת שליחה (From)</Label>
              <Input
                dir="ltr"
                value={brevoFromEmail}
                onChange={(e) => setBrevoFromEmail(e.target.value)}
                placeholder="shanydadon@gmail.com"
                className="mt-1 text-right"
                data-testid="popup-brevo-from-email"
              />
              <p className="text-[10px] text-stone-500 mt-1">הכתובת שרשמתם איתה ב-Brevo, אפשר Gmail רגיל.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddEmailOpen(false)} data-testid="add-email-cancel">ביטול</Button>
            <Button
              onClick={async () => {
                if (!brevoApiKey || !brevoFromEmail) {
                  toast.error("חובה למלא את שני השדות");
                  return;
                }
                setAddingEmail(true);
                try {
                  await api.patch(`/companies/${companyId}`, {
                    email_provider: "brevo",
                    brevo_api_key: brevoApiKey,
                    brevo_from_email: brevoFromEmail,
                    channels: Array.from(new Set([...(company?.channels || []), "email"])),
                  });
                  const { data } = await api.get("/companies");
                  const c = (data.companies || []).find((x) => x.id === companyId);
                  setCompany(c || null);
                  setSendEmailToo(true);
                  setAddEmailOpen(false);
                  setBrevoApiKey(""); setBrevoFromEmail("");
                  toast.success("✓ מסלול מיילים חובר! מעכשיו יישלחו גם אימיילים ללקוחות עם כתובת.");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "שגיאה בחיבור");
                } finally {
                  setAddingEmail(false);
                }
              }}
              disabled={addingEmail || !brevoApiKey || !brevoFromEmail}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="add-email-save"
            >
              {addingEmail ? "מחבר..." : "חבר ושלח"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        </div> {/* end main content column */}
      </div> {/* end two-column grid */}
    </div>
  );
}

function StatusPill({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
        ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
      }`}
    >
      <span className={`h-2 w-2 rounded-full ${ok ? "bg-green-500" : "bg-red-500"}`}></span>
      {label} • {ok ? "מחובר" : "לא מחובר"}
    </span>
  );
}

function ModeBanner({ health, onRefresh }) {
  const [switching, setSwitching] = useState(false);
  const isProd = health?.twilio_balance?.is_production;
  const fromNum = health?.twilio_balance?.from_number;

  if (!health) return null;

  const handleSwitch = async (toMode) => {
    setSwitching(true);
    try {
      await switchTwilioMode(toMode);
      toast.success(toMode === "sandbox" ? "🧪 עברת למצב טסט" : "🚀 עברת למצב פרודקשן");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה במעבר מצב");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div
      className={`rounded-xl p-3 flex items-center justify-between flex-wrap gap-3 border-2 ${
        isProd ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"
      }`}
      data-testid="mode-banner"
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl">{isProd ? "🚀" : "🧪"}</span>
        <div>
          <p className="font-bold text-sm">
            {isProd ? "מצב פרודקשן פעיל" : "מצב טסט (Sandbox) פעיל"}
          </p>
          <p className="text-[11px] text-stone-600">
            שולח מ: <code dir="ltr" className="bg-white px-1.5 py-0.5 rounded font-mono text-[10px]">{fromNum}</code>
            {isProd
              ? " — שליחה מלאה ללא הגבלות"
              : " — רק לנמענים שעשו join לסנדבוקס"}
          </p>
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={switching}
        onClick={() => handleSwitch(isProd ? "sandbox" : "production")}
        className={isProd ? "border-amber-500 text-amber-800 hover:bg-amber-100" : "border-emerald-500 text-emerald-800 hover:bg-emerald-100"}
        data-testid="toggle-mode-btn"
      >
        {switching ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : (
          isProd ? "🧪 עברי לטסט" : "🚀 עברי לפרודקשן"
        )}
      </Button>
    </div>
  );
}

function SandboxNotice({ twilioBalance }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("sandbox-notice-dismissed") === "1"; } catch { return false; }
  });
  const [copied, setCopied] = useState(false);
  // Only show for Sandbox accounts (trial/sandbox typical indicator: from_number contains "14155238886" or no explicit production flag)
  const isSandbox = !twilioBalance?.is_production; // treat unknown / undefined as sandbox to be safe

  if (dismissed || !isSandbox) return null;

  const dismiss = () => {
    try { localStorage.setItem("sandbox-notice-dismissed", "1"); } catch {}
    setDismissed(true);
  };

  const copyCode = () => {
    navigator.clipboard.writeText("join discuss-anyone");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex gap-3 relative"
      data-testid="sandbox-notice"
    >
      <Info className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
      <div className="text-sm flex-1">
        <p className="font-bold text-amber-900">⚠️ חשוב לדעת - מצב Sandbox של Twilio</p>
        <p className="text-amber-900 mt-1 leading-relaxed">
          כרגע המערכת עובדת במצב ניסיון (Sandbox). <strong>כל לקוח חייב לבצע פעולה חד-פעמית</strong> לפני שניתן לשלוח לו:
        </p>
        <ol className="list-decimal pr-5 mt-2 space-y-0.5 text-amber-900">
          <li>להיכנס לוואטסאפ ולשלוח הודעה למספר <strong dir="ltr" className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">+1 415 523 8886</strong></li>
          <li>
            בתוכן ההודעה לכתוב:
            <button type="button"
              onClick={copyCode}
              className="mx-1 inline-flex items-center gap-1 bg-white hover:bg-amber-100 px-2 py-0.5 rounded font-mono text-xs border border-amber-300"
              data-testid="copy-sandbox-code"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              join discuss-anyone
            </button>
          </li>
          <li>רק לאחר אישור הצטרפות הלקוח - המערכת תצליח לשלוח לו חשבוניות.</li>
        </ol>
        <p className="text-xs text-amber-800 mt-2 leading-relaxed">
          💡 זו הסיבה שההודעה נשלחה רק לחלק מהלקוחות - רק הם הצטרפו ל-Sandbox.
          <br />
          <strong>לפתרון קבוע:</strong> רישום למספר WhatsApp Business רשמי דרך Meta (ללא הגבלה זו).
        </p>
      </div>
      <button type="button"
        onClick={dismiss}
        className="absolute top-2 left-2 text-amber-700 hover:text-amber-900 text-xs font-semibold"
        data-testid="dismiss-sandbox-notice"
      >
        ✕
      </button>
    </div>
  );
}

function SentSummary({ results, onNewBatch }) {
  const total = Object.keys(results).length;
  const success = Object.values(results).filter((r) => r.whatsapp_ok || r.email_ok).length;
  const failed = total - success;
  const allOk = failed === 0 && total > 0;
  const allFailed = success === 0 && total > 0;

  // Three states:
  //   • allOk     → blue checkmark "נשלחו בהצלחה"
  //   • allFailed → red error "שגיאה מלאה"
  //   • mixed     → amber warning "שגיאה חלקית"
  const palette = allOk
    ? { bg: "bg-blue-50", border: "border-blue-500", icon: "bg-blue-500", IconComp: CheckCheck }
    : allFailed
    ? { bg: "bg-red-50", border: "border-red-500", icon: "bg-red-500", IconComp: AlertTriangle }
    : { bg: "bg-amber-50", border: "border-amber-400", icon: "bg-amber-500", IconComp: AlertTriangle };
  const headline = allOk
    ? `נשלחו בהצלחה ${success} מסמכים`
    : allFailed
    ? `שגיאה מלאה — לא נשלחו מסמכים`
    : `שגיאה חלקית — ${success} נשלחו, ${failed} נכשלו`;

  return (
    <div
      className={`rounded-2xl p-5 shadow-lg flex items-center justify-between flex-wrap gap-3 border-2 ${palette.bg} ${palette.border}`}
      data-testid="sent-summary"
    >
      <div className="flex items-center gap-3">
        <div
          className={`h-12 w-12 rounded-full flex items-center justify-center ${palette.icon} text-white shadow-md`}
        >
          <palette.IconComp className="h-6 w-6" />
        </div>
        <div>
          <p className="font-heading text-lg font-bold text-stone-900">
            {headline}
          </p>
          <p className="text-sm text-stone-600 mt-0.5">
            <strong>{success}/{total}</strong>
            {failed > 0 && <span className="text-red-600"> • {failed} נכשלו</span>}
          </p>
        </div>
      </div>
      <Button variant="outline" onClick={onNewBatch} data-testid="new-batch-from-summary">
        <RotateCcw className="h-4 w-4 ml-2" />
        שליחה חדשה
      </Button>
    </div>
  );
}
